/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useEffect, useRef } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  openDocumentByIdThunk,
  isDocumentVersionType,
  selectDocumentVersionById,
  selectMessages,
  selectPublishedOnly,
  setDocumentToSearchFor,
  textInputThunk,
  toggleOptionKey,
} from './chatSlice';
import {
  setBrowserTitle,
  useAudienceFromSearchParamsOrLocalStorage,
  useNavigateToTopic,
  useReplaceCurrentTopic,
  useSearchParamsDocument,
} from './useHooks';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { Chat } from './Chat';
import { SearchCategoriesContext } from './hooks/MessageContext';
import styles from './ChatComponent.module.scss';
import AudienceContainer from './components/Audience/AudienceContainer';
import { createRandomAlphaNumericId } from '../utils';

interface ChatComponentProps {
  creatorMode?: boolean;
  searchCategories?: boolean;
  initialSearch?: string;
}

export function ChatComponent({ creatorMode, searchCategories, initialSearch }: ChatComponentProps) {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectMessages);
  const disableAudience = true;
  const [audienceFilter, setAudienceFilter] = useAudienceFromSearchParamsOrLocalStorage(true, disableAudience);
  const currentSearch = useAppSelector((state) => state.chat.currentSearch);
  const compact = useMediaQuery('(max-width: 900px)');

  const chatRef = useRef(null);

  const publishedOnly = useAppSelector(selectPublishedOnly);
  const navigateToTopic = useNavigateToTopic(publishedOnly);
  const replaceCurrentTopic = useReplaceCurrentTopic(publishedOnly);

  const lastMessage = messages[messages.length - 1];
  const documentVersionId = messages.find((m) => m.pages?.length)?.pages?.[0]?.document_version_id;
  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, documentVersionId));
  const chatRunReferrenceRef = useRef<string | null>(null);

  /*  This will call: openDocumentByIdThunk if a document id is passed in the url search param,
   *  and call textInputThunk if text is passed in the url.
   */
  useSearchParamsDocument(audienceFilter, publishedOnly, !!searchCategories);

  useEffect(() => {
    // For Microsoft Teams bot, we pass which document to open in the subEntityId
    // And then pass that to this component as initialSearch
    if (initialSearch) {
      console.log('initialSearch', initialSearch);
      const [text, persona, careCategory, patientGroup] = initialSearch.split('|');
      dispatch(
        setDocumentToSearchFor({
          audience: {
            persona,
            careCategory,
            patientGroup,
          },
          text,
          publishedOnly,
          searchCategories: false,
          howActivated: 'teamsBot',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearch]);

  // Called only when the user actually types in the search text
  function sendText(text: string, slug: string | undefined) {
    if (text) navigateToTopic(text, slug, 'userTyped');
    return Promise.resolve();
  }
  useEffect(() => {
    // cleanup for setDocumentToSearchFor
    return () => {
      dispatch(setDocumentToSearchFor());
      chatRunReferrenceRef.current = null;
      sessionStorage.removeItem('algorithmRunReferrenceId');
    };
  }, [dispatch]);
  /*Adding this use effect as a part of PoC:https://pearl-project.atlassian.net/browse/PRL-12950*/
  useEffect(() => {
    if (
      documentVersion &&
      !creatorMode &&
      (documentVersion.document.slug === 'antenatal_corticosteroid_acs_blue_box_program' ||
        documentVersion.document.slug === 'antenatal_corticosteroid_acs_blue_box_program_testing' ||
        documentVersion.document.slug === 'antenatal_corticosteroid_blue_box_program')
    ) {
      /*Add a unique identifier for the current chat component session run for the given doc.
       */
      if (chatRunReferrenceRef.current === null) {
        const id = createRandomAlphaNumericId();
        chatRunReferrenceRef.current = id;
        sessionStorage.setItem('algorithmRunReferrenceId', chatRunReferrenceRef.current);
      }
    }
    // Remove this as we navigate away from the document.
    if (
      documentVersion?.title !== 'Antenatal Corticosteroid (ACS) Blue Box Program' &&
      documentVersion?.title !== 'Antenatal Corticosteroid (ACS) Blue Box Program-testing'
    ) {
      chatRunReferrenceRef.current = null;
      sessionStorage.removeItem('algorithmRunReferrenceId');
    }
  }, [documentVersion, creatorMode]);

  const external_link = lastMessage?.extra?.type === 'OPEN_LINK' ? lastMessage?.extra?.external_link : undefined;
  if (external_link?.link && external_link?.link[0] !== '#') {
    // Sanity check
    throw new Error('external_link.link must start with a #');
  }

  useEffect(() => {
    if (currentSearch?.text) {
      dispatch(textInputThunk(currentSearch))
        .unwrap()
        .then((result) => {
          setBrowserTitle(`${currentSearch.text}`);
          const resolvedVersion =
            result?.versions.length === 1 && isDocumentVersionType(result.versions[0]!)
              ? result.versions[0]
              : undefined;
          if (!currentSearch.slug && resolvedVersion?.document.slug) {
            replaceCurrentTopic(resolvedVersion.document.slug, resolvedVersion.title);
          }
        })
        .catch((e) => {
          console.log('error in textInputThunk', e);
        });
    } else if (currentSearch?.documentVersionId || currentSearch?.documentId || currentSearch?.slug) {
      dispatch(
        openDocumentByIdThunk({
          documentId: currentSearch.documentId,
          documentVersionId: currentSearch.documentVersionId,
          slug: currentSearch.slug,
          page: currentSearch.page,
          audienceFilter,
          publishedOnly,
        })
      )
        .unwrap()
        .catch((error) => {
          console.log('error in openDocumentByIdThunk', error);
        });
    }
  }, [audienceFilter, currentSearch, dispatch, publishedOnly, replaceCurrentTopic]);

  useEffect(() => {
    // Called only when the user was in an algorithm, and then clicked on a button to open a new guide
    if (external_link) {
      navigateToTopic(external_link.link.slice(1), external_link.link_slug, 'algorithmClick', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [external_link]);

  return (
    <SearchCategoriesContext.Provider value={!!searchCategories}>
      <AudienceContainer
        audienceFilter={audienceFilter}
        publishedOnly={publishedOnly}
        chatRef={chatRef}
        documentVersion={documentVersion}
        onChangeAudience={setAudienceFilter}
        showPublishedOnly={creatorMode}
        showFeedback={!creatorMode}
        compact={compact}
      >
        <Chat
          audienceFilter={audienceFilter}
          allowPageDelete={false}
          allowPageChangeRoot={false}
          publishedOnly={publishedOnly}
          className={styles.chat}
          messages={messages}
          onKeyToggled={(page, messageIndex, key, value) =>
            dispatch(toggleOptionKey({ page, messageIndex, key, value }))
          }
          onTextInput={sendText}
          compact={compact}
          inAlgorithmView={false}
          chatRef={chatRef}
        />
      </AudienceContainer>
    </SearchCategoriesContext.Provider>
  );
}
